import type { PaletteEntry } from './types';
import type { SupportPlan } from './support';

class NbtWriter {
  private buffer = new Uint8Array(1024);
  private length = 0;

  private ensure(extra: number) {
    if (this.length + extra <= this.buffer.length) return;
    let size = this.buffer.length * 2;
    while (size < this.length + extra) size *= 2;
    const grown = new Uint8Array(size);
    grown.set(this.buffer.subarray(0, this.length));
    this.buffer = grown;
  }

  byte(v: number) {
    this.ensure(1);
    this.buffer[this.length++] = v & 0xff;
    return this;
  }

  short(v: number) {
    this.ensure(2);
    this.buffer[this.length++] = (v >> 8) & 0xff;
    this.buffer[this.length++] = v & 0xff;
    return this;
  }

  int(v: number) {
    this.ensure(4);
    this.buffer[this.length++] = (v >>> 24) & 0xff;
    this.buffer[this.length++] = (v >>> 16) & 0xff;
    this.buffer[this.length++] = (v >>> 8) & 0xff;
    this.buffer[this.length++] = v & 0xff;
    return this;
  }

  string(v: string) {
    const bytes = new TextEncoder().encode(v);
    this.short(bytes.length);
    this.ensure(bytes.length);
    this.buffer.set(bytes, this.length);
    this.length += bytes.length;
    return this;
  }

  bytes(v: Uint8Array) {
    this.int(v.length);
    this.ensure(v.length);
    this.buffer.set(v, this.length);
    this.length += v.length;
    return this;
  }

  tag(type: number, name: string) {
    this.byte(type);
    this.string(name);
    return this;
  }

  result(): Uint8Array {
    return this.buffer.slice(0, this.length);
  }
}

export interface SchematicLayout {
  width: number;
  height: number;
  length: number;
  blocks: Uint8Array;
  data: Uint8Array;
  supportPlaced: number;
  nooblinePlaced: number;
}

export interface SchematicInput {
  width: number;
  length: number;
  pixels: Int32Array;
  entries: PaletteEntry[];
  heights: Int32Array;
  nooblineHeights: Int32Array;
  support: SupportPlan;
  supportBlock: { id: number; meta: number };
  maxY: number;
  /** Off when the user builds without a noobline, so the top row shades against the world. */
  includeNoobline?: boolean;
}

/** How far the build has to lift so the deepest support block still fits above the base. */
export function schematicShift(input: SchematicInput): number {
  let shift = 0;
  forEachSchematicColumn(input, 0, (column) => {
    shift = Math.max(shift, column.supportDepth - column.y);
  });
  return shift;
}

/**
 * One column of the build: its top block at `y`, and `supportDepth` support blocks straight below.
 * Schematic z 0 is the noobline row when there is one, so every map row sits one block south of it.
 */
export interface SchematicColumn {
  x: number;
  y: number;
  z: number;
  supportDepth: number;
  block: { id: number; meta: number };
  /** Null for the noobline, which is built from the support block. */
  entry: PaletteEntry | null;
}

export function schematicSize(input: SchematicInput, shift: number) {
  const rowOffset = input.includeNoobline !== false ? 1 : 0;
  return { width: input.width, height: input.maxY + shift + 1, length: input.length + rowOffset };
}

/** Every column of the build, the one place its geometry is decided. `shift` lifts it all. */
export function forEachSchematicColumn(
  input: SchematicInput,
  shift: number,
  visit: (column: SchematicColumn) => void,
) {
  const { width, length, pixels, entries, heights, nooblineHeights, support, supportBlock } = input;
  const noobline = input.includeNoobline !== false;
  const rowOffset = noobline ? 1 : 0;
  for (let x = 0; x < width; x++) {
    if (noobline) {
      visit({
        x,
        y: nooblineHeights[x] + shift,
        z: 0,
        supportDepth: support.nooblineCounts[x],
        block: supportBlock,
        entry: null,
      });
    }
    for (let z = 0; z < length; z++) {
      const p = z * width + x;
      const entryIndex = pixels[p];
      if (entryIndex < 0) continue;
      const entry = entries[entryIndex];
      visit({ x, y: heights[p] + shift, z: z + rowOffset, supportDepth: support.counts[p], block: entry.block, entry });
    }
  }
}

/** Lays the artwork out as MCEdit block/data arrays, lifted far enough for the deepest support block. */
export function buildSchematicLayout(input: SchematicInput): SchematicLayout {
  const shift = schematicShift(input);
  const size = schematicSize(input, shift);
  const volume = size.width * size.height * size.length;
  const blocks = new Uint8Array(volume);
  const data = new Uint8Array(volume);

  let supportPlaced = 0;
  let nooblinePlaced = 0;

  const place = (x: number, y: number, z: number, id: number, meta: number) => {
    const i = (y * size.length + z) * size.width + x;
    blocks[i] = id & 0xff;
    data[i] = meta & 0xf;
  };

  forEachSchematicColumn(input, shift, (column) => {
    place(column.x, column.y, column.z, column.block.id, column.block.meta);
    if (column.entry === null) nooblinePlaced++;
    for (let depth = 1; depth <= column.supportDepth; depth++) {
      place(column.x, column.y - depth, column.z, input.supportBlock.id, input.supportBlock.meta);
      supportPlaced++;
    }
  });

  return { ...size, blocks, data, supportPlaced, nooblinePlaced };
}

export function encodeSchematicNbt(layout: SchematicLayout): Uint8Array {
  const w = new NbtWriter();
  w.tag(10, 'Schematic');
  w.tag(2, 'Width').short(layout.width);
  w.tag(2, 'Height').short(layout.height);
  w.tag(2, 'Length').short(layout.length);
  w.tag(8, 'Materials').string('Alpha');
  w.tag(7, 'Blocks').bytes(layout.blocks);
  w.tag(7, 'Data').bytes(layout.data);
  w.tag(9, 'Entities').byte(10).int(0);
  w.tag(9, 'TileEntities').byte(10).int(0);
  w.byte(0);
  return w.result();
}

export async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
  const packed = await new Response(stream).arrayBuffer();
  return new Uint8Array(packed);
}

export async function encodeSchematic(layout: SchematicLayout): Promise<Uint8Array> {
  return gzip(encodeSchematicNbt(layout));
}
